import { SelectionModel } from '@angular/cdk/collections';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { debounce } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { AutocompleteModule } from '../../../../components/autocomplete/autocomplete.module';
import { BoxModule } from '../../../../components/box/box.module';
import { t } from '../../../../components/translate/translate.function';
import { TranslateModule } from '../../../../components/translate/translate.module';
import { AutoFocusDirectiveModule } from '../../../../directives/auto-focus/auto-focus..directive.module';
import { Case } from '../../../../models/case';
import { snack, snackErr } from '../../../../modules/snack.module';
import { CaseService } from '../../case.service';
import { MergedCasesListItemComponent } from '../list-item/merged-cases-list-item';
import { MergedCase } from '../merged-case.type';
import { CaseMergeInDialogData } from './merge-in-dialog-data';
import { CaseMergeInDialogResult } from './merge-in-dialog-result';

@Component({
  selector: 'apex-case-merge-in-dialog',
  templateUrl: './merge-in-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    AutoFocusDirectiveModule,
    AutocompleteModule,
    BoxModule,
    MergedCasesListItemComponent,
    ScrollingModule,
    MatDividerModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
  ],
  providers: [CaseService],
})
export class CaseMergeInDialogComponent implements OnInit {
  searchText = '';
  cases: Case[] = [];
  mergeDescriptions = true;

  loading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: CaseMergeInDialogData,
    private ref: MatDialogRef<CaseMergeInDialogComponent, CaseMergeInDialogResult>,
    private caseService: CaseService,
  ) {}

  selectionList: SelectionModel<MergedCase> = new SelectionModel<MergedCase>(
    true,
    [],
    undefined,
    (a, b) => a.id === b.id,
  );

  ngOnInit(): void {
    if (this.data?.selectedCases) {
      this.selectionList.select(...this.data.selectedCases);
    }

    void this.doSearch();
  }

  close(): void {
    this.ref.close();
  }

  async onSearchChange(text: string, forceSearch = false): Promise<void> {
    this.cases = [];

    if (!forceSearch && (!text || text.length < 3)) {
      return;
    }

    await this.doSearch(text);
  }

  async doSearch(text?: string): Promise<void> {
    this.loading = true;

    const cases = await firstValueFrom(
      this.caseService.getCases({
        showOpen: true,
        showCompleted: true,
        showCaseManagers: true,
        textSearch: text,
        Project: this.data.ProjectId,
        Apartment: this.data.ApartmentId,
        Object: this.data.ObjectId,
      }),
    );

    this.loading = false;

    this.cases = (cases || []).filter((c) => c.id !== this.data.caseId);
  }

  debouncedSearchChange = debounce((text: string, forceSearch = false) => {
    void this.onSearchChange(text, forceSearch);
  }, 300);

  async merge(): Promise<void> {
    const casesToMerge = this.selectionList.selected;

    if (!casesToMerge.length) {
      snackErr(t('No cases selected to merge'), new Error('No cases selected'));

      return;
    }

    if (this.data?.onlyReturnData) {
      this.ref.close({
        caseId: this.data.caseId,
        casesToMerge,
        mergeDescriptions: this.mergeDescriptions,
      });

      return;
    }

    if (!this.data.caseId) {
      snackErr(t('Current case ID is missing'), new Error('Current case ID is missing'));

      return;
    }

    try {
      const resp = await firstValueFrom(
        this.caseService.mergeCases(
          this.data.caseId,
          casesToMerge.map((c) => c.id),
          this.mergeDescriptions,
        ),
      );

      if (resp) {
        snack(t('Cases merged successfully'));

        this.ref.close({
          caseId: this.data.caseId,
          casesToMerge,
          mergeDescriptions: this.mergeDescriptions,
        });
      } else {
        snackErr(t('Merging cases failed'), new Error('Merging cases failed'));

        this.ref.close();
      }
    } catch (err) {
      snackErr(t('Merging cases failed'), err);
    }
  }
}
